import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import useDebounce from 'hooks/useDebounce'
import { DATA_ACCESS_REQUEST_STUDY_ID_VALIDATION_ERROR } from 'modules/requests/data-access-requests/DataAccessRequestDetails/constants'
import { getDataAccessRequestStudyIdEvaluationAlerts } from 'modules/requests/data-access-requests/DataAccessRequestDetails/getDataAccessRequestStudyIdEvaluationAlert'
import { evaluateDataAccessRequestStudyId } from 'services/data-access-requests'
import { StudyIdEvaluationOutcome } from 'services/data-access-requests/enums'
import { DataAccessRequestQueryKeys } from 'services/data-access-requests/queryKeys'
import type { StudyIdEvaluationResult } from 'services/data-access-requests/types'
import { studyIdSchema } from 'services/shared/schemas/studyIdSchema'
import { normalizeStudyIdInput } from 'services/shared/studyIdUtils'

const STUDY_ID_VALIDATION_DEBOUNCE_MS = 500

interface UseDebouncedDataAccessRequestStudyIdValidationArgs {
  enabled: boolean
  requestId: string
  studyId: string
}

export const useDebouncedDataAccessRequestStudyIdValidation = ({
  enabled,
  requestId,
  studyId
}: UseDebouncedDataAccessRequestStudyIdValidationArgs) => {
  const normalizedStudyId = normalizeStudyIdInput(studyId)
  const debouncedStudyId = useDebounce(enabled ? normalizedStudyId : '', STUDY_ID_VALIDATION_DEBOUNCE_MS)

  const isCurrentValueSchemaValid = useMemo(
    () => normalizedStudyId.length > 0 && studyIdSchema.isValidSync(normalizedStudyId),
    [normalizedStudyId]
  )

  const isDebouncedValueSchemaValid = useMemo(
    () => debouncedStudyId.length > 0 && studyIdSchema.isValidSync(debouncedStudyId),
    [debouncedStudyId]
  )

  const isDebouncedValueReady = enabled && normalizedStudyId === debouncedStudyId
  const isValidationEnabled = enabled && isDebouncedValueSchemaValid && isDebouncedValueReady

  const evaluationQuery = useQuery<StudyIdEvaluationResult>({
    queryKey: DataAccessRequestQueryKeys.StudyIdEvaluation(requestId, debouncedStudyId),
    queryFn: ({ signal }) => evaluateDataAccessRequestStudyId(requestId, { studyId: debouncedStudyId }, { signal }),
    enabled: isValidationEnabled,
    retry: false
  })

  const evaluationAlerts = useMemo(
    () => getDataAccessRequestStudyIdEvaluationAlerts(evaluationQuery.data, debouncedStudyId, requestId),
    [debouncedStudyId, evaluationQuery.data, requestId]
  )

  const validationErrorMessage = useMemo(() => {
    if (evaluationQuery.error === null || evaluationQuery.error === undefined) {
      return undefined
    }

    return DATA_ACCESS_REQUEST_STUDY_ID_VALIDATION_ERROR
  }, [evaluationQuery.error])

  const isValidating = isValidationEnabled && evaluationQuery.isFetching

  const isValidationPending =
    enabled && isCurrentValueSchemaValid && (normalizedStudyId !== debouncedStudyId || evaluationQuery.isFetching)

  const hasConflict = evaluationQuery.data?.outcome === StudyIdEvaluationOutcome.Conflict

  const canApprove =
    isValidationEnabled &&
    !evaluationQuery.isFetching &&
    evaluationQuery.isSuccess &&
    !hasConflict &&
    validationErrorMessage === undefined

  return {
    canApprove,
    evaluationAlerts,
    evaluationResult: evaluationQuery.data,
    isValidating,
    isValidationPending,
    validationErrorMessage
  }
}
